import { useState } from 'react';
import { Autocomplete, Chip, FormControlLabel, Switch, TextField } from '@mui/material';
import { SCHEDULER_LABEL_CLASS } from './schedulerFieldClasses';
import { endsInsideEntry, recipientsIn } from './recipientAddresses';

const SEPARATOR_KEYS = ['Enter', ',', ';'];
// What a browser sends for a key that confirms an IME composition; Safari no longer marks that key as composing.
const IME_KEY_CODE = 229;

export interface ScheduleRecipientsFieldsProps {
  viewKey: string;
  recipients: string[];
  recipientInput: string;
  notAdded: string[];
  sendToSelf: boolean;
  disabled: boolean;
  onRecipientsChange: (recipients: string[]) => void;
  onRecipientInputChange: (input: string) => void;
  onNotAddedChange: (words: string[]) => void;
  onSendToSelfChange: (sendToSelf: boolean) => void;
  recipientsLabel?: string;
  sendToSelfLabel?: string;
}

/** The recipient email chips and the switch that adds the current user to them. */
export const ScheduleRecipientsFields = ({
  viewKey,
  recipients,
  recipientInput,
  notAdded,
  sendToSelf,
  disabled,
  onRecipientsChange,
  onRecipientInputChange,
  onNotAddedChange,
  onSendToSelfChange,
  recipientsLabel = 'Recipients',
  sendToSelfLabel = 'Also send a copy to me'
}: ScheduleRecipientsFieldsProps) => {
  // The text this field kept to fix, until it is edited: Enter on it unchanged leaves it out.
  const [kept, setKept] = useState<string | null>(null);

  const leaveOut = () => {
    onRecipientInputChange('');
    onNotAddedChange([]);
    setKept(null);
  };

  // What is not taken is named rather than becoming a recipient, and kept to fix only where reading it again adds nothing.
  const addTypedRecipients = () => {
    const read = recipientsIn(recipientInput);
    const listed = new Set(recipients.map((recipient) => recipient.toLowerCase()));
    const added = read.addresses.filter((address) => {
      const isNew = !listed.has(address.toLowerCase());
      listed.add(address.toLowerCase());
      return isNew;
    });
    onRecipientsChange([...recipients, ...added]);
    const toFix = read.toFix.join(' ');
    // An address already among the chips needs no naming, wherever this read found it.
    const notAddedNow = read.notAdded.filter((word) => !listed.has(word.toLowerCase()));
    onRecipientInputChange(toFix);
    onNotAddedChange(notAddedNow);
    setKept(notAddedNow.length > 0 ? toFix : null);
  };
  const isKeptUnchanged = notAdded.length > 0 && recipientInput === kept;

  return (
    <>
      <div className="mb-3">
        <label
          htmlFor={`recipients-input-${viewKey}`}
          className={SCHEDULER_LABEL_CLASS}>
          {recipientsLabel}
        </label>
        <Autocomplete<string, true, false, true>
          id={`recipients-input-${viewKey}`}
          multiple
          freeSolo
          disabled={disabled}
          options={[]}
          value={recipients}
          inputValue={recipientInput}
          onInputChange={(_e, value, reason) => {
            onRecipientInputChange(value);
            if (reason === 'input') {
              onNotAddedChange([]);
              setKept(null);
            }
          }}
          onChange={(_e, value) => onRecipientsChange(value)}
          onKeyDown={(e) => {
            if (!SEPARATOR_KEYS.includes(e.key)) return;
            const event = e as typeof e & { defaultMuiPrevented: boolean };
            // The Autocomplete reads a composing Enter by its key code alone, and would commit the text being composed.
            if (e.nativeEvent.isComposing || e.keyCode === IME_KEY_CODE) {
              event.defaultMuiPrevented = true;
              return;
            }
            // Inside a quoted name, a comment or angle brackets, a comma or semicolon is part of what is being typed.
            if (e.key !== 'Enter' && endsInsideEntry(recipientInput)) return;
            e.preventDefault();
            if (e.key === 'Enter' && isKeptUnchanged) {
              event.defaultMuiPrevented = true;
              leaveOut();
              return;
            }
            // An empty input leaves the key to the Autocomplete, which moves focus off a selected chip with it.
            if (recipientInput === '') return;
            // Otherwise the Autocomplete adds what was typed as one chip too, and its value replaces the split one.
            event.defaultMuiPrevented = true;
            addTypedRecipients();
          }}
          onBlur={() => {
            if (recipientInput.trim() && !isKeptUnchanged) addTypedRecipients();
          }}
          renderTags={(value, getTagProps) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '4px 0' }}>
              {value.map((option, index) => {
                const tagProps = getTagProps({ index });
                return (
                  <Chip
                    {...tagProps}
                    key={tagProps.key}
                    label={option}
                    size="small"
                    sx={{ fontSize: '0.75rem', height: 24 }}
                  />
                );
              })}
            </div>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={recipients.length === 0 ? 'Type email and press Enter' : ''}
              error={notAdded.length > 0}
              helperText={
                notAdded.length > 0
                  ? `Not added: ${notAdded.join(', ')}. Correct the field, or press Enter to leave ${notAdded.length === 1 ? 'it' : 'them'} out.`
                  : undefined
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem',
                  minHeight: 36,
                  alignItems: 'center',
                  padding: '4px 8px'
                }
              }}
            />
          )}
        />
      </div>

      <div className="flex flex-col items-start">
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              size="small"
              checked={sendToSelf}
              disabled={disabled}
              onChange={(e) => onSendToSelfChange(e.target.checked)}
            />
          }
          label={<span className="text-sm">{sendToSelfLabel}</span>}
        />
      </div>
    </>
  );
};
